import React, { useCallback, useEffect, useRef, useState } from 'react';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';

/**
 * Eden AI chat — the new provider-agnostic chat UI (task eden-api-mcp-ai-2026-07-10).
 * Talks to /api/v1/ai/* (EdenAiController): conversation list, SSE-streamed
 * assistant turns, tool-call cards, and write-approval cards resolved inline.
 * Replaces the polling legacy AiChat when AI_SYSTEM=eden.
 */

const md = new MarkdownIt({ linkify: true, breaks: true });

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

async function api(method, url, body) {
    const response = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
            ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(payload?.error?.message ?? `Request failed (${response.status})`);
    }
    return payload;
}

function Markdown({ text }) {
    const html = DOMPurify.sanitize(md.render(String(text ?? '')));
    // eslint-disable-next-line react/no-danger
    return <div className="eden-md" dangerouslySetInnerHTML={{ __html: html }} />;
}

function ToolCard({ event, onResolve }) {
    const pending = event.result?.pending_approval;
    return (
        <div
            style={{
                border: '1px solid #ddd', borderRadius: 6, padding: '6px 10px',
                margin: '4px 0', background: pending ? '#fff8e1' : '#f5f7fa', fontSize: 13,
            }}
        >
            <strong>{pending ? '⏸ Approval needed' : '🔧 Tool'}:</strong>{' '}
            <code>{event.name}</code>
            {event.arguments && Object.keys(event.arguments).length > 0 && (
                <details style={{ display: 'inline-block', marginLeft: 8 }}>
                    <summary style={{ cursor: 'pointer', display: 'inline' }}>args</summary>
                    <pre style={{ fontSize: 11, margin: '4px 0 0' }}>{JSON.stringify(event.arguments, null, 2)}</pre>
                </details>
            )}
            {pending && onResolve && (
                <span style={{ marginLeft: 10 }}>
                    <button type="button" className="btn btn-success btn-xs" onClick={() => onResolve(event.result.action_id, true)}>
                        Approve &amp; run
                    </button>{' '}
                    <button type="button" className="btn btn-default btn-xs" onClick={() => onResolve(event.result.action_id, false)}>
                        Deny
                    </button>
                </span>
            )}
        </div>
    );
}

export default function EdenChat() {
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [streamText, setStreamText] = useState('');
    const [liveEvents, setLiveEvents] = useState([]);
    const [pendingActions, setPendingActions] = useState([]);
    const scrollRef = useRef(null);

    const loadConversations = useCallback(async () => {
        try {
            const payload = await api('GET', '/api/v1/ai/conversations?per_page=50');
            setConversations(payload.data ?? []);
            return payload.data ?? [];
        } catch (e) {
            setError(e.message);
            return [];
        }
    }, []);

    const loadMessages = useCallback(async (conversationId) => {
        try {
            const payload = await api('GET', `/api/v1/ai/conversations/${conversationId}/messages`);
            setMessages(payload.data ?? []);
        } catch (e) {
            setError(e.message);
        }
    }, []);

    const loadPending = useCallback(async () => {
        try {
            const payload = await api('GET', '/api/v1/ai/actions/pending');
            setPendingActions(payload.data ?? []);
        } catch {
            /* non-fatal */
        }
    }, []);

    useEffect(() => {
        (async () => {
            const list = await loadConversations();
            if (list.length > 0) {
                setActiveId(list[0].id);
            }
            await loadPending();
        })();
    }, [loadConversations, loadPending]);

    useEffect(() => {
        if (activeId) {
            loadMessages(activeId);
        } else {
            setMessages([]);
        }
    }, [activeId, loadMessages]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    }, [messages, streamText, liveEvents]);

    const newConversation = async () => {
        try {
            const payload = await api('POST', '/api/v1/ai/conversations', {});
            await loadConversations();
            setActiveId(payload.data.id);
        } catch (e) {
            setError(e.message);
        }
    };

    const removeConversation = async (conversationId) => {
        if (!window.confirm('Delete this conversation?')) {
            return;
        }
        try {
            await api('DELETE', `/api/v1/ai/conversations/${conversationId}`);
            const list = await loadConversations();
            setActiveId((current) => (current === conversationId ? (list[0]?.id ?? null) : current));
        } catch (e) {
            setError(e.message);
        }
    };

    const resolveAction = async (actionId, approve) => {
        try {
            await api('POST', `/api/v1/ai/actions/${actionId}/resolve`, { approve });
            await loadPending();
            if (activeId) {
                await loadMessages(activeId);
            }
        } catch (e) {
            setError(e.message);
        }
    };

    const send = async (event) => {
        event.preventDefault();
        if (!draft.trim() || busy) {
            return;
        }

        let conversationId = activeId;
        if (!conversationId) {
            const payload = await api('POST', '/api/v1/ai/conversations', {});
            conversationId = payload.data.id;
            setActiveId(conversationId);
        }

        const content = draft.trim();
        setDraft('');
        setBusy(true);
        setError(null);
        setStreamText('');
        setLiveEvents([]);
        setMessages((current) => [...current, { id: `tmp-${Date.now()}`, role: 'user', content }]);

        try {
            const response = await fetch(`/api/v1/ai/conversations/${conversationId}/messages`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'text/event-stream',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken(),
                },
                body: JSON.stringify({ content, stream: true }),
            });

            if (!response.ok || !response.body) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.error?.message ?? `Request failed (${response.status})`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                buffer += decoder.decode(value, { stream: true });

                let newlineIndex;
                // eslint-disable-next-line no-cond-assign
                while ((newlineIndex = buffer.indexOf('\n\n')) !== -1) {
                    const raw = buffer.slice(0, newlineIndex).trim();
                    buffer = buffer.slice(newlineIndex + 2);
                    if (!raw.startsWith('data:')) {
                        continue;
                    }
                    let payload;
                    try {
                        payload = JSON.parse(raw.slice(5).trim());
                    } catch {
                        continue;
                    }

                    if (payload.type === 'delta') {
                        setStreamText((current) => current + payload.content);
                    } else if (payload.type === 'tool_call' || payload.type === 'tool_result') {
                        setLiveEvents((current) => [...current, payload]);
                    } else if (payload.type === 'error') {
                        setError(payload.message);
                    }
                }
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
            setStreamText('');
            setLiveEvents([]);
            await loadMessages(conversationId);
            await loadConversations();
            await loadPending();
        }
    };

    const renderStoredMessage = (message) => (
        <div key={message.id} style={{ margin: '10px 0' }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: message.role === 'user' ? '#31708f' : '#3c763d' }}>
                {message.role === 'user' ? 'You' : 'Assistant'}
                {message.status === 'error' && <span className="text-danger"> — failed: {message.error}</span>}
            </div>
            {(message.tool_results ?? []).map((toolResult, index) => (
                <ToolCard
                    key={index}
                    event={{ name: toolResult.tool, result: toolResult.result }}
                    onResolve={resolveAction}
                />
            ))}
            <Markdown text={message.content} />
        </div>
    );

    return (
        <div style={{ display: 'flex', gap: 16, minHeight: '70vh' }}>
            <div style={{ width: 260, flexShrink: 0, borderRight: '1px solid #e5e5e5', paddingRight: 12 }}>
                <button type="button" className="btn btn-primary btn-sm btn-block" onClick={newConversation}>
                    + New conversation
                </button>
                <ul className="list-unstyled" style={{ marginTop: 10 }}>
                    {conversations.map((conversation) => (
                        <li
                            key={conversation.id}
                            style={{
                                padding: '6px 8px', borderRadius: 4, cursor: 'pointer',
                                background: conversation.id === activeId ? '#e8f0fe' : 'transparent',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}
                        >
                            <span
                                role="button"
                                tabIndex={0}
                                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
                                onClick={() => setActiveId(conversation.id)}
                                onKeyDown={(e) => e.key === 'Enter' && setActiveId(conversation.id)}
                            >
                                {conversation.title}
                            </span>
                            <button
                                type="button"
                                className="btn btn-link btn-xs text-danger"
                                title="Delete"
                                onClick={() => removeConversation(conversation.id)}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {pendingActions.length > 0 && (
                    <div className="alert alert-warning" style={{ marginBottom: 10 }}>
                        <strong>{pendingActions.length} action{pendingActions.length > 1 ? 's' : ''} awaiting approval.</strong>
                        {pendingActions.map((action) => (
                            <div key={action.id} style={{ marginTop: 6 }}>
                                <code>{action.tool}</code>{' '}
                                <button type="button" className="btn btn-success btn-xs" onClick={() => resolveAction(action.id, true)}>
                                    Approve &amp; run
                                </button>{' '}
                                <button type="button" className="btn btn-default btn-xs" onClick={() => resolveAction(action.id, false)}>
                                    Deny
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {error && <div className="alert alert-danger">{error}</div>}

                <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '0 4px' }}>
                    {messages.length === 0 && !busy && (
                        <p className="text-muted" style={{ marginTop: 30, textAlign: 'center' }}>
                            Ask anything about your account — the assistant can search records,
                            read and update data (with your approval for changes), and work
                            through your processes.
                        </p>
                    )}
                    {messages.map(renderStoredMessage)}
                    {busy && (
                        <div style={{ margin: '10px 0' }}>
                            <div style={{ fontWeight: 600, fontSize: 12, color: '#3c763d' }}>Assistant</div>
                            {liveEvents.filter((event) => event.type === 'tool_call').map((event, index) => (
                                <ToolCard key={index} event={event} />
                            ))}
                            {streamText
                                ? <Markdown text={streamText} />
                                : <em className="text-muted">Thinking…</em>}
                        </div>
                    )}
                </div>

                <form onSubmit={send} style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <textarea
                        className="form-control"
                        rows={2}
                        value={draft}
                        placeholder="Message the assistant… (Enter to send, Shift+Enter for newline)"
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                send(e);
                            }
                        }}
                    />
                    <button type="submit" className="btn btn-primary" disabled={busy || !draft.trim()}>
                        {busy ? '…' : 'Send'}
                    </button>
                </form>
            </div>
        </div>
    );
}
